import React from "react";
import { Link } from "react-router-dom";
import { LandingPageLayout } from "../shared/layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Reveal } from "@/components/motion";
import { useTranslation } from "react-i18next";
import { Check, ArrowRight } from "lucide-react";

export function PricingPageScreen() {
  const { t } = useTranslation();
  const plans = t("landing.pricing.plans", { returnObjects: true });

  return (
    <LandingPageLayout>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-[linear-gradient(180deg,hsl(var(--accent)/0.7),hsl(var(--background))_58%,hsl(var(--secondary)/0.45))]" />
        <div className="container mx-auto px-4 py-20 md:py-24">
          <Reveal className="text-center max-w-2xl mx-auto mb-4">
            <Badge variant="secondary" className="mb-5 rounded-full border border-primary/15">{t("landing.pricing.hero.badge")}</Badge>
            <h1 className="text-4xl md:text-5xl font-semibold tracking-tight mb-5">
              {t("landing.pricing.hero.title")}
            </h1>
            <p className="text-lg text-muted-foreground">
              {t("landing.pricing.hero.description")}
            </p>
          </Reveal>

          <Reveal delay={0.1} className="max-w-md mx-auto mb-12">
            <div className="rounded-lg border bg-card px-4 py-3 text-center text-sm text-muted-foreground">
              {t("landing.pricing.beta.before")} <span className="text-foreground font-medium">{t("landing.pricing.beta.highlight")}</span> {t("landing.pricing.beta.after")}
            </div>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {plans.map((plan, i) => (
              <Reveal key={plan.name} delay={i * 0.1}>
                <div
                  className={`rounded-lg border bg-card p-7 h-full flex flex-col ${
                    plan.featured ? "border-primary border-2 shadow-lg relative" : ""
                  }`}
                >
                  {plan.featured && (
                    <span className="absolute -top-3 left-7 bg-primary text-primary-foreground text-xs font-medium px-3 py-1 rounded-full">
                      {plan.badge}
                    </span>
                  )}
                  <div className="mb-5">
                    <h3 className="text-lg font-medium mb-1">{plan.name}</h3>
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-semibold" style={{ fontFamily: '"Fraunces", serif' }}>{plan.price}</span>
                      <span className="text-muted-foreground">{plan.cadence}</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-2">{plan.blurb}</p>
                  </div>
                  <ul className="space-y-3 mb-7 flex-1">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-start gap-3 text-sm">
                        <span className="mt-0.5 w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                          <Check className="h-3 w-3 text-primary" />
                        </span>
                        {f}
                      </li>
                    ))}
                  </ul>
                  <Button
                    className="w-full"
                    variant={plan.featured ? "default" : "outline"}
                    asChild
                  >
                    <Link to="/register">{plan.cta} <ArrowRight className="ml-2 h-4 w-4" /></Link>
                  </Button>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.15} className="text-center mt-12 max-w-xl mx-auto">
            <p className="text-sm text-muted-foreground">
              {t("landing.pricing.footerNote")}
            </p>
          </Reveal>
        </div>
      </section>
    </LandingPageLayout>
  );
}
